// ─────────────────────────────────────────────────────────────────────────
// <solar-calculator> — the Shadow-DOM web component. Zero runtime deps. Themed
// entirely through --sc-* CSS custom properties; configured via attributes,
// a `.config` property, or window.SolarCalculator.configure().
// ─────────────────────────────────────────────────────────────────────────

import tokensCss from '../styles/tokens.css?inline';
import elementCss from '../styles/element.css?inline';

import { calc, deemingYearsFor, estimateAnnualUsageKwh, estimateBatteryRebate, annualiseBill, recommendSystemSize } from '../core/calc.js';
import { auditFreshness, formatAsOf } from '../core/freshness.js';
import { loadPostcodeTable, lookupPostcode } from '../core/postcode.js';
import type { PostcodeTable } from '../core/postcode.js';
import type { CalcResult, PostcodeZone, Scenario } from '../core/types.js';
import { defaultConfig, listSourced, resolveConfig } from '../config/defaults.js';
import type { AudienceMode, ClientConfig, LossOption, SolarCalcConfig } from '../config/schema.js';
import { buildCrmPayload, submitLead } from './leadForm.js';
import type { LeadDetails } from './leadForm.js';
import { inputsHtml, leadFormHtml, modeTabsHtml, resultsHtml } from './ui/templates.js';

/** Resolved per-mode settings (mode overrides layered over `assumptions`). */
interface ResolvedMode {
  name: AudienceMode;
  label: string;
  gstRegistered: boolean;
  systemSizesKw: readonly number[];
  sizeSliderMin: number;
  sizeSliderMax: number;
  sizeSliderStep: number;
  defaultSizeKw: number;
  costPerKw: number;
  retailRateFactor: number;
  daytimeLoadShareByProfile: Record<string, number>;
  defaultBillPeriod: Scenario['billPeriod'];
  note?: string;
  title: string;
  intro: string;
  billLabel: string;
  billHint: string;
  billDefault: number;
  billMax: number;
  billStep: number;
  profileLabel: string;
  profiles: Array<{ value: string; label: string }>;
  costLabel: string;
  quoteHeading: string;
}

// Global config set by the embed via window.SolarCalculator.configure().
let globalConfig: ClientConfig = {};
export function setGlobalConfig(c: ClientConfig): void {
  globalConfig = c;
}

const DEBOUNCE_MS = 220;

export class SolarCalculatorElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['postcode', 'webhook-url', 'phone', 'quote-url', 'client-name', 'data-url', 'audience'];
  }

  private _config: ClientConfig = {};
  private resolved: SolarCalcConfig = defaultConfig;
  private mode: AudienceMode = 'residential';
  private sharedParamsApplied = false;
  private table: PostcodeTable | null = null;
  private zone: PostcodeZone | null = null;
  private view: 'solar' | 'battery' = 'solar';
  private touched = new Set<string>();
  private lastResult: CalcResult | null = null;
  private lastScenario: Scenario | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private mounted = false;

  /** Rich config (npm/Astro consumers): el.config = {...}. */
  set config(c: ClientConfig) {
    this._config = c ?? {};
    if (this.mounted) this.reconfigure();
  }
  get config(): ClientConfig {
    return this._config;
  }

  connectedCallback(): void {
    this.mounted = true;
    if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
    this.reconfigure();
  }

  attributeChangedCallback(): void {
    if (this.mounted) this.reconfigure();
  }

  // ── config resolution: defaults ← global ← property ← attributes ──
  private reconfigure(): void {
    const attrOverrides: ClientConfig = { client: {} };
    const cn = this.getAttribute('client-name');
    const ph = this.getAttribute('phone');
    const qu = this.getAttribute('quote-url');
    const wh = this.getAttribute('webhook-url');
    const du = this.getAttribute('data-url');
    const aud = this.getAttribute('audience');
    if (cn) attrOverrides.client!.clientName = cn;
    if (ph) attrOverrides.client!.phone = ph;
    if (qu) attrOverrides.client!.quoteUrl = qu;
    if (wh != null) attrOverrides.client!.webhookUrl = wh;
    if (du) attrOverrides.dataUrl = du;
    if (aud === 'residential' || aud === 'commercial' || aud === 'both') {
      attrOverrides.audience = aud;
    }

    // Merge in order: national defaults ← global ← property ← attributes.
    this.resolved = resolveConfig(deepMergeAll(globalConfig, this._config, attrOverrides));

    // Single-audience configs pin the mode; 'both' keeps the user's selection.
    if (this.resolved.audience === 'commercial') this.mode = 'commercial';
    else if (this.resolved.audience === 'residential') this.mode = 'residential';

    this.render();

    // A shared link's query params take precedence over attribute defaults.
    const fromShare = !this.sharedParamsApplied && this.applySharedParams();
    if (fromShare) return;

    const pc = this.getAttribute('postcode');
    if (pc) {
      const input = this.shadowRoot!.querySelector<HTMLInputElement>('[data-sc="postcode"]');
      if (input) input.value = pc;
      void this.onPostcode(pc);
    }
  }

  // Every public theme token, so re-theming (e.g. switching presets) clears any
  // token the previous theme set but the new one omits — no lingering flourishes.
  private static readonly THEME_TOKENS = [
    '--sc-accent', '--sc-accent-ink', '--sc-ink', '--sc-ink-soft', '--sc-surface',
    '--sc-paper', '--sc-muted', '--sc-edge', '--sc-positive', '--sc-radius',
    '--sc-font', '--sc-font-mono', '--sc-shell-shadow', '--sc-btn-bg', '--sc-hero-bg',
  ];

  private applyTheme(): void {
    const theme = this.resolved.theme as Record<string, string | undefined>;
    for (const k of SolarCalculatorElement.THEME_TOKENS) {
      const v = theme[k];
      if (v) this.style.setProperty(k, v);
      else this.style.removeProperty(k);
    }
  }

  private render(): void {
    this.applyTheme();
    const c = this.resolved;
    this.shadowRoot!.innerHTML = `
      <style>${tokensCss}\n${elementCss}</style>
      <div class="sc-root">
        <div class="sc-head">
          <h2 class="sc-title">${escapeHtml(c.copy.heading)}</h2>
          <p class="sc-intro">${escapeHtml(c.copy.intro)}</p>
        </div>
        ${c.audience === 'both' ? modeTabsHtml(c, this.mode) : ''}
        <div class="sc-error sc-hidden" data-sc-error role="alert"></div>
        ${inputsHtml(c)}
        <div class="sc-results sc-hidden" data-sc-results aria-live="polite"></div>
        <div class="sc-lead-wrap sc-hidden" data-sc-leadwrap></div>
        <div class="sc-toast" data-sc-toast role="status" aria-live="polite"></div>
      </div>`;
    this.wire();
    this.applyModeToInputs();
  }

  /** Resolve the active mode's settings (mode overrides over `assumptions`). */
  private resolveMode(): ResolvedMode {
    const c = this.resolved;
    const m = c.modes[this.mode] ?? c.modes.residential;
    const a = c.assumptions;
    const shares: Record<string, number> = { ...a.daytimeLoadShareByProfile };
    for (const [k, v] of Object.entries(m.daytimeLoadShareByProfile ?? {})) {
      if (typeof v === 'number') shares[k] = v;
    }
    return {
      name: this.mode,
      label: m.label,
      gstRegistered: m.gstRegistered,
      systemSizesKw: m.systemSizesKw ?? a.systemSizesKw,
      sizeSliderMin: m.sizeSliderMin ?? 3,
      sizeSliderMax: m.sizeSliderMax ?? 20,
      sizeSliderStep: m.sizeSliderStep ?? 0.1,
      defaultSizeKw: m.defaultSizeKw ?? c.defaults.sizeKw,
      costPerKw: m.costPerKw ?? a.costPerKw,
      retailRateFactor: m.retailRateFactor ?? 1,
      daytimeLoadShareByProfile: shares,
      defaultBillPeriod: m.defaultBillPeriod ?? c.defaults.billPeriod,
      note: m.note,
      title: m.title ?? c.copy.heading,
      intro: m.intro ?? c.copy.intro,
      billLabel: m.billLabel ?? 'Electricity bill',
      billHint: m.billHint ?? 'Drag to adjust — or type an exact amount above.',
      billDefault: m.billDefault ?? 400,
      billMax: m.billMax ?? 1500,
      billStep: m.billStep ?? 25,
      profileLabel: m.profileLabel ?? 'When do you mostly use power?',
      profiles: m.profiles ?? [{ value: c.defaults.usageProfile, label: 'Mixed use' }],
      costLabel: m.costLabel ?? 'Solar cost ($/kW)',
      quoteHeading: m.quoteHeading ?? c.copy.leadHeading,
    };
  }

  /** Apply the active mode's copy, profile options and input ranges to the DOM. */
  private applyModeToInputs(): void {
    const mode = this.resolveMode();
    const root = this.shadowRoot!;
    const setText = (sel: string, text: string): void => {
      const el = root.querySelector(sel);
      if (el) el.textContent = text;
    };

    // Copy
    setText('.sc-title', mode.title);
    setText('.sc-intro', mode.intro);
    setText('[data-sc-billlabel]', mode.billLabel);
    setText('[data-sc-billhint]', mode.billHint);
    setText('[data-sc-profilelabel]', mode.profileLabel);
    setText('[data-sc-costlabel]', mode.costLabel);
    setText('[data-sc-leadheading]', mode.quoteHeading);

    // Size slider range
    const slider = root.querySelector<HTMLInputElement>('[data-sc="sizeKw"]');
    if (slider) {
      slider.min = String(mode.sizeSliderMin);
      slider.max = String(mode.sizeSliderMax);
      slider.step = String(mode.sizeSliderStep);
    }

    // Bill slider range + default (only re-default bill if the user hasn't typed one)
    const billRange = root.querySelector<HTMLInputElement>('[data-sc-billrange]');
    if (billRange) {
      billRange.max = String(mode.billMax);
      billRange.step = String(mode.billStep);
    }
    if (!this.touched.has('bill')) {
      const bill = root.querySelector<HTMLInputElement>('[data-sc="bill"]');
      if (bill) bill.value = String(mode.billDefault);
      if (billRange) billRange.value = String(mode.billDefault);
    }

    // Cost default + bill period (unless the user overrode them)
    if (!this.touched.has('costPerKw')) {
      const cost = root.querySelector<HTMLInputElement>('[data-sc="costPerKw"]');
      if (cost) cost.value = String(mode.costPerKw);
    }
    if (!this.touched.has('billPeriod')) {
      const per = root.querySelector<HTMLSelectElement>('[data-sc="billPeriod"]');
      if (per) per.value = mode.defaultBillPeriod;
    }

    // Profile options (re-render, preserving the current value if still valid)
    const profile = root.querySelector<HTMLSelectElement>('[data-sc="usageProfile"]');
    if (profile) {
      const prev = profile.value;
      profile.innerHTML = mode.profiles
        .map((p) => `<option value="${p.value}">${escapeHtml(p.label)}</option>`)
        .join('');
      if (mode.profiles.some((p) => p.value === prev)) profile.value = prev;
    }
  }

  private wire(): void {
    const root = this.shadowRoot!;
    // Recompute on any input change.
    root.querySelectorAll<HTMLElement>('[data-sc]').forEach((el) => {
      const key = el.getAttribute('data-sc')!;
      const evt = el instanceof HTMLInputElement && (el.type === 'range' || el.type === 'number' || el.type === 'text') ? 'input' : 'change';
      el.addEventListener(evt, () => {
        if (key !== 'postcode') this.touched.add(key);
        if (key === 'sizeKw') {
          const sv = root.querySelector('[data-sc-sizeval]');
          if (sv) sv.textContent = `${(el as HTMLInputElement).value} kW`;
        }
        if (key === 'bill') {
          const range = root.querySelector<HTMLInputElement>('[data-sc-billrange]');
          if (range) range.value = (el as HTMLInputElement).value;
        }
        if (key === 'postcode') {
          this.scheduleDebounced(() => this.onPostcode((el as HTMLInputElement).value));
        } else {
          this.scheduleDebounced(() => this.recompute());
        }
      });
    });

    // Bill slider ↔ number input mirror (manual entry is the source of truth).
    const range = root.querySelector<HTMLInputElement>('[data-sc-billrange]');
    const billInput = root.querySelector<HTMLInputElement>('[data-sc="bill"]');
    if (range && billInput) {
      range.addEventListener('input', () => {
        billInput.value = range.value;
        this.touched.add('bill');
        this.scheduleDebounced(() => this.recompute());
      });
    }

    // Residential / Commercial segmented control.
    root.querySelectorAll<HTMLButtonElement>('[data-sc-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = btn.getAttribute('data-sc-mode') as AudienceMode;
        if (next === this.mode) return;
        this.mode = next;
        root.querySelectorAll('[data-sc-mode]').forEach((b) =>
          b.setAttribute('aria-pressed', String(b.getAttribute('data-sc-mode') === next)),
        );
        // Mode changes copy, ranges, $/kW, bill defaults, the small-business rate
        // factor and the GST basis — reset the mode-driven inputs so they re-default.
        ['sizeKw', 'costPerKw', 'bill', 'billPeriod', 'retailRate'].forEach((k) => this.touched.delete(k));
        this.applyModeToInputs();
        if (this.zone) this.prefillRegionRates(this.zone);
        this.recompute();
      });
    });
  }

  /** Map a select's value to the production factor from its config option list. */
  private lossFactor(key: string, options: LossOption[], fallback: number): number {
    const el = this.shadowRoot!.querySelector<HTMLSelectElement>(`[data-sc="${key}"]`);
    if (!el) return fallback;
    return options.find((o) => o.value === el.value)?.factor ?? fallback;
  }

  private scheduleDebounced(fn: () => void): void {
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(fn, DEBOUNCE_MS);
  }

  private showError(msg: string): void {
    const e = this.shadowRoot!.querySelector('[data-sc-error]')!;
    e.textContent = msg;
    e.classList.remove('sc-hidden');
  }
  private clearError(): void {
    this.shadowRoot!.querySelector('[data-sc-error]')!.classList.add('sc-hidden');
  }

  private async onPostcode(value: string): Promise<void> {
    const region = this.shadowRoot!.querySelector('[data-sc-region]');
    if (!/^\d{4}$/.test(value.trim())) {
      this.zone = null;
      if (region) region.textContent = '';
      return;
    }
    try {
      if (!this.table) {
        this.table = await loadPostcodeTable(this.resolved.dataUrl);
      }
      const zone = lookupPostcode(this.table, value);
      if (!zone) {
        this.zone = null;
        if (region) region.textContent = 'Postcode not found — check it and try again.';
        return;
      }
      this.zone = zone;
      if (region) region.textContent = `${zone.state} · zone ${zone.stcZone}`;
      this.prefillRegionRates(zone);
      this.clearError();
      this.recompute();
    } catch {
      this.showError('Could not load postcode data. Please try again shortly.');
    }
  }

  private regionRates(zone: PostcodeZone): { retailRate: number; feedInTariff: number; supplyChargePerDay: number } {
    const r =
      this.resolved.regions[zone.distributorRegion] ??
      this.resolved.regions[zone.state] ??
      this.resolved.regions['NSW']!;
    // Commercial mode: use the region's explicit small-business usage charge +
    // supply (AER DMO small business) where present; else the residential rate ×
    // the mode fallback factor. FiT uses the same benchmark for both.
    if (this.mode === 'commercial') {
      const factor = this.resolveMode().retailRateFactor;
      return {
        retailRate: r.commercialRetailRate?.value ?? r.retailRate.value * factor,
        feedInTariff: r.feedInTariff.value,
        supplyChargePerDay: r.commercialSupplyChargePerDay?.value ?? r.supplyChargePerDay.value,
      };
    }
    return {
      retailRate: r.retailRate.value,
      feedInTariff: r.feedInTariff.value,
      supplyChargePerDay: r.supplyChargePerDay.value,
    };
  }

  private prefillRegionRates(zone: PostcodeZone): void {
    const rates = this.regionRates(zone); // already mode-aware
    const set = (key: string, val: number): void => {
      if (this.touched.has(key)) return;
      const el = this.shadowRoot!.querySelector<HTMLInputElement>(`[data-sc="${key}"]`);
      if (el) el.value = String(val);
    };
    set('retailRate', rates.retailRate);
    set('feedInTariff', rates.feedInTariff);
    set('supplyChargePerDay', rates.supplyChargePerDay);
  }

  private num(key: string, fallback: number): number {
    const el = this.shadowRoot!.querySelector<HTMLInputElement>(`[data-sc="${key}"]`);
    const v = el ? parseFloat(el.value) : NaN;
    return Number.isFinite(v) ? v : fallback;
  }
  private str(key: string, fallback: string): string {
    const el = this.shadowRoot!.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-sc="${key}"]`);
    return el && el.value ? el.value : fallback;
  }
  private bool(key: string): boolean {
    const el = this.shadowRoot!.querySelector<HTMLInputElement>(`[data-sc="${key}"]`);
    return !!el && el.checked;
  }

  private recompute(): void {
    if (!this.zone) return;
    const c = this.resolved;
    const mode = this.resolveMode();
    const rates = this.regionRates(this.zone);
    const bill = this.num('bill', 0);
    const billPeriod = this.str('billPeriod', c.defaults.billPeriod) as Scenario['billPeriod'];
    const usageProfile = this.str('usageProfile', c.defaults.usageProfile) as Scenario['usageProfile'];
    const batteryInterest = this.bool('batteryInterest');

    // Recommended size from usage; drive the slider until the user touches it.
    const annualBill = annualiseBill(bill, billPeriod);
    const retailRate = this.num('retailRate', rates.retailRate);
    const supply = this.num('supplyChargePerDay', rates.supplyChargePerDay);
    const usageKwh = estimateAnnualUsageKwh(annualBill, supply, retailRate);

    // Roof orientation + shade selectors → the SAME factors the tooltip prints.
    const performanceRatio = this.num('performanceRatio', c.assumptions.performanceRatio);
    const orientationFactor = this.lossFactor('orientation', c.orientationOptions, c.assumptions.orientationFactor);
    const shadeFactor = this.lossFactor('shade', c.shadeOptions, c.assumptions.shadeFactor);
    const daytimeLoadShare = mode.daytimeLoadShareByProfile[usageProfile] ?? 0.4;

    const genPerKwYr = this.zone.zoneRatingKwhPerKw * performanceRatio || 1300;
    const recommended = recommendSystemSize(
      usageKwh,
      usageProfile,
      genPerKwYr,
      mode.systemSizesKw,
    );
    if (!this.touched.has('sizeKw')) {
      const slider = this.shadowRoot!.querySelector<HTMLInputElement>('[data-sc="sizeKw"]');
      const sval = this.shadowRoot!.querySelector('[data-sc-sizeval]');
      if (slider) slider.value = String(recommended);
      if (sval) sval.textContent = `${recommended} kW`;
    }

    const installYear = c.defaults.installYear;
    const deemingYears = deemingYearsFor(installYear, c.sresEndYear.value);
    const deemingTable = c.battery.stcDeemingPerKwhByYear.value;
    const batteryDeeming = deemingTable[String(installYear)] ?? Object.values(deemingTable).slice(-1)[0] ?? 0;
    const usableKwh = this.num('batteryUsableKwh', c.defaults.battery.defaultUsableKwh);

    const scenario: Scenario = {
      stcZone: this.zone.stcZone,
      zoneRatingKwhPerKw: this.zone.zoneRatingKwhPerKw,
      deemingYears,
      stcPrice: c.stcPrice.value,
      retailRate,
      feedInTariff: this.num('feedInTariff', rates.feedInTariff),
      supplyChargePerDay: supply,
      bill,
      billPeriod,
      usageProfile,
      daytimeLoadShare,
      hasExistingSolar: this.bool('hasExistingSolar'),
      gstRegistered: mode.gstRegistered,
      sizeKw: this.num('sizeKw', recommended),
      costPerKw: this.num('costPerKw', mode.costPerKw),
      performanceRatio,
      orientationFactor,
      shadeFactor,
      exportLimitKw: null,
      panelWatts: c.assumptions.panelWatts,
      systemSizesKw: mode.systemSizesKw,
      battery: batteryInterest
        ? {
            usableKwh,
            costPerKwh: c.battery.costPerKwh.value,
            rebate: estimateBatteryRebate(usableKwh, batteryDeeming, c.stcPrice.value, c.battery.taperBands),
            roundTripEfficiency: c.defaults.battery.roundTripEfficiency,
          }
        : null,
      electricityInflation: c.assumptions.electricityInflation,
      fitDecline: c.assumptions.fitDecline,
      fitFloor: c.assumptions.fitFloor,
      firstYearDegradation: c.assumptions.firstYearDegradation,
      degradation: c.assumptions.degradation,
      emissionsFactorKgPerKwh: c.emissionsFactorKgPerKwh.value,
      discountRate: c.assumptions.discountRate,
    };

    if (!batteryInterest && this.view === 'battery') this.view = 'solar';

    const result = calc(scenario, c.version);
    this.lastResult = result;
    this.lastScenario = scenario;
    this.renderResults();
  }

  private renderResults(): void {
    if (!this.lastResult) return;
    const c = this.resolved;
    const today = new Date().toISOString().slice(0, 10);
    const asOf = formatAsOf(auditFreshness(listSourced(c), today).maxLastUpdated);

    const mode = this.resolveMode();
    const resultsEl = this.shadowRoot!.querySelector('[data-sc-results]')!;
    resultsEl.innerHTML = resultsHtml(c, this.lastResult, this.view, asOf, {
      gstRegistered: mode.gstRegistered,
      note: mode.note,
    });
    resultsEl.classList.remove('sc-hidden');

    // view toggle
    resultsEl.querySelectorAll<HTMLButtonElement>('[data-sc-view]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.view = btn.getAttribute('data-sc-view') as 'solar' | 'battery';
        this.renderResults();
      });
    });

    // lead form + save/share toolbar (rendered once results exist)
    const leadWrap = this.shadowRoot!.querySelector('[data-sc-leadwrap]')!;
    if (leadWrap.classList.contains('sc-hidden')) {
      leadWrap.innerHTML = leadFormHtml(c);
      leadWrap.classList.remove('sc-hidden');
      this.wireLeadForm();
    }
  }

  private wireLeadForm(): void {
    const root = this.shadowRoot!;
    const form = root.querySelector<HTMLFormElement>('[data-sc-lead]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        void this.submitLeadForm(form);
      });
    }
    // Save / share toolbar.
    root.querySelector('[data-sc-pdf]')?.addEventListener('click', () => window.print());
    root.querySelector('[data-sc-copylink]')?.addEventListener('click', () => void this.copyLink());
    root.querySelector('[data-sc-share]')?.addEventListener('click', () => void this.shareResults());
  }

  private async submitLeadForm(form: HTMLFormElement): Promise<void> {
    const err = form.querySelector('[data-sc-lead-error]')!;
    const ok = form.querySelector('[data-sc-lead-success]')!;
    const get = (n: string): string => (form.querySelector<HTMLInputElement>(`[name="${n}"]`)?.value ?? '').trim();
    const name = get('name');
    const phone = get('phone');
    const address = get('address');
    if (!name || !phone || !address) {
      err.classList.remove('sc-hidden');
      return;
    }
    err.classList.add('sc-hidden');

    if (!this.lastResult || !this.lastScenario || !this.zone) return;
    const lead: LeadDetails = {
      name,
      phone,
      address,
      email: get('email') || undefined,
      roofType: get('roofType') || undefined,
    };
    const payload = buildCrmPayload({
      config: this.resolved,
      scenario: this.lastScenario,
      result: this.lastResult,
      lead,
      mode: this.mode,
      postcode: this.str('postcode', ''),
      state: this.zone.state,
      distributorRegion: String(this.zone.distributorRegion),
      batteryInterest: this.bool('batteryInterest'),
      location: { href: location.href, search: location.search },
      now: new Date().toISOString(),
    });

    const res = await submitLead(this.resolved.client.webhookUrl, payload);
    const btn = form.querySelector('button[type="submit"]');
    btn?.setAttribute('disabled', 'true');
    ok.classList.remove('sc-hidden');
    ok.textContent = res.demo
      ? this.resolved.copy.demoNotice
      : res.ok
        ? `Thanks ${name.split(' ')[0]} — we’ll be in touch within one business day.`
        : 'Thanks — we received your details (delivery is being confirmed).';
    (ok as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ── shareable links ───────────────────────────────────────────────────────
  /** Compact param keys ↔ field keys for the share URL. */
  private static readonly SHARE_KEYS: Array<[string, string]> = [
    ['pc', 'postcode'], ['bill', 'bill'], ['per', 'billPeriod'], ['prof', 'usageProfile'],
    ['or', 'orientation'], ['sh', 'shade'], ['size', 'sizeKw'], ['bat', 'batteryUsableKwh'],
  ];

  /** Build a shareable URL encoding the current inputs. */
  private shareUrl(): string {
    const params = new URLSearchParams();
    params.set('sc_mode', this.mode);
    for (const [p, key] of SolarCalculatorElement.SHARE_KEYS) {
      const el = this.shadowRoot!.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-sc="${key}"]`);
      if (el && el.value) params.set(`sc_${p}`, el.value);
    }
    if (this.bool('hasExistingSolar')) params.set('sc_solar', '1');
    if (this.bool('batteryInterest')) params.set('sc_battery', '1');
    return `${location.origin}${location.pathname}?${params.toString()}`;
  }

  /** A transient toast at the bottom of the element. */
  private showToast(message: string): void {
    const toast = this.shadowRoot!.querySelector('[data-sc-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('sc-toast--visible');
    setTimeout(() => toast.classList.remove('sc-toast--visible'), 2400);
  }

  /** Copy the shareable link to the clipboard (with a legacy fallback). */
  private async copyLink(): Promise<void> {
    const url = this.shareUrl();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const tmp = document.createElement('input');
      tmp.value = url;
      this.shadowRoot!.appendChild(tmp);
      tmp.select();
      try { document.execCommand('copy'); } catch { /* no-op */ }
      tmp.remove();
    }
    this.showToast(this.resolved.copy.shareCopiedLabel);
  }

  /** Native share sheet where available; otherwise fall back to copy. */
  private async shareResults(): Promise<void> {
    const url = this.shareUrl();
    const r = this.lastResult?.solar;
    const text = r
      ? `Solar estimate: ${r.sizeKw} kW, ${r.paybackYears ?? '—'} yr payback, ~$${Math.round(r.year1Savings).toLocaleString('en-AU')}/yr saving`
      : 'Solar payback estimate';
    const nav = navigator as Navigator & { share?: (d: unknown) => Promise<void> };
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: 'Solar payback estimate', text, url });
      } catch {
        /* user cancelled — no-op */
      }
    } else {
      await this.copyLink();
    }
  }

  /** Pre-fill inputs from a shared link's query params. Returns true if applied. */
  private applySharedParams(): boolean {
    this.sharedParamsApplied = true;
    let search = '';
    try {
      search = location.search;
    } catch {
      return false;
    }
    const q = new URLSearchParams(search);
    if (![...q.keys()].some((k) => k.startsWith('sc_'))) return false;

    const root = this.shadowRoot!;
    const mode = q.get('sc_mode');
    if ((mode === 'residential' || mode === 'commercial') && this.resolved.audience === 'both') {
      this.mode = mode;
      root.querySelectorAll('[data-sc-mode]').forEach((b) =>
        b.setAttribute('aria-pressed', String(b.getAttribute('data-sc-mode') === mode)),
      );
    }
    this.applyModeToInputs();

    for (const [p, key] of SolarCalculatorElement.SHARE_KEYS) {
      const v = q.get(`sc_${p}`);
      if (v == null) continue;
      const el = root.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-sc="${key}"]`);
      if (el) {
        el.value = v;
        if (key !== 'postcode') this.touched.add(key);
      }
    }
    const setCheck = (param: string, key: string): void => {
      if (q.get(param) === '1') {
        const el = root.querySelector<HTMLInputElement>(`[data-sc="${key}"]`);
        if (el) el.checked = true;
      }
    };
    setCheck('sc_solar', 'hasExistingSolar');
    setCheck('sc_battery', 'batteryInterest');

    const pc = q.get('sc_pc');
    if (pc) void this.onPostcode(pc);
    return true;
  }
}

// ── helpers ───────────────────────────────────────────────────────────────
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function deepMergeAll(...layers: ClientConfig[]): ClientConfig {
  return layers.reduce<ClientConfig>((acc, layer) => deepMergeObj(acc, layer), {});
}
function deepMergeObj(a: ClientConfig, b: ClientConfig): ClientConfig {
  const out: Record<string, unknown> = { ...(a as Record<string, unknown>) };
  for (const [k, v] of Object.entries(b as Record<string, unknown>)) {
    const av = out[k];
    if (isObj(av) && isObj(v)) out[k] = deepMergeObj(av as ClientConfig, v as ClientConfig);
    else if (v !== undefined) out[k] = v;
  }
  return out as ClientConfig;
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Register the custom element once. */
export function defineSolarCalculator(tag = 'solar-calculator'): void {
  if (typeof customElements !== 'undefined' && !customElements.get(tag)) {
    customElements.define(tag, SolarCalculatorElement);
  }
}
